import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { RectangleHorizontal, DoorOpen, Wind, Trash2, Calculator, Ruler, Target, X, Plus, Eraser, Maximize2, Minimize2 } from 'lucide-react';
import { findRoomAtPoint } from '../utils/floorPlanUtils';

interface FloorPlanWindow {
  id: string;
  width: number; // in mm
  height: number; // in mm
  floor: number;
  orientation?: string;
  roomName?: string; // Room context for better identification
  wallId?: string; // Wall association for room lookup
  position?: { x: number; y: number }; // Position for room lookup
}

interface PropertyElevationDiagramProps {
  elevationType: string;
  elevationId: string | number; // Unique ID for elevation (can be numeric ID or type string)
  elevationNumber: number;
  propertyData: {
    propertyType: string;
    roofTypeRdSAP: string;
    groundLevel: string;
    detachment: string;
    floorCount: number;
  };
  wallInsulationType?: string;
  initialPlacedElements?: PlacedElement[]; // Add prop for restored placed elements
  initialDimensions?: { height: number; width: number }; // Add prop for restored dimensions
  floorPlanWindows?: FloorPlanWindow[]; // Add prop for floor plan windows
  walls?: any[]; // Wall data for room lookup
  roomCustomLabels?: Record<string, string>; // Room names for context
  currentRooms?: any[]; // Current rooms for position-based lookup
  onElevationDataChange?: (elevationId: string | number, data: {
    wallArea: number;
    untreatableArea: number;
    treatableArea: number;
    dimensions: {
      height: number;
      width: number;
    };
    elements: {
      doors: Array<{
        id: string;
        height: number;
        width: number;
      }>;
      windows: Array<{
        id: string;
        height: number;
        width: number;
      }>;
      airVents: Array<{
        id: string;
      }>;
    };
    placedElements: PlacedElement[];
    wallInsulationType?: string;
    buildingHeight: number;
  }) => void;
}

interface PlacedElement {
  id: string;
  type: 'window' | 'door' | 'airvent';
  x: number;
  y: number;
  elevation: string;
  height?: number;
  width?: number;
}

interface ElevationDimensions {
  height: number;
  width: number;
}

type ToolType = 'window' | 'door' | 'airvent' | 'eraser' | null;

// Custom Window Form Component
const CustomWindowForm: React.FC<{ onAdd: (width: number, height: number) => void }> = ({ onAdd }) => {
  const [width, setWidth] = useState<string>('1.5');
  const [height, setHeight] = useState<string>('1.0');

  const handleAdd = () => {
    const widthNum = parseFloat(width);
    const heightNum = parseFloat(height);
    
    if (widthNum > 0 && heightNum > 0) {
      onAdd(widthNum, heightNum);
      setWidth('1.5'); // Reset to defaults
      setHeight('1.0');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Width (m)
          </label>
          <input
            type="number"
            step="0.01"
            min="0.1"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="1.5"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Height (m)
          </label>
          <input
            type="number"
            step="0.01"
            min="0.1"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="1.0"
          />
        </div>
      </div>
      
      <div className="bg-slate-50 rounded-lg p-3">
        <div className="text-sm text-slate-600 mb-1">Preview:</div>
        <div className="font-medium text-slate-800">
          {parseFloat(width || '0').toFixed(2)}m × {parseFloat(height || '0').toFixed(2)}m
          {width && height && (
            <span className="text-slate-500 ml-2">
              ({(parseFloat(width) * parseFloat(height)).toFixed(2)}m²)
            </span>
          )}
        </div>
      </div>
      
      <button
        type="button"
        onClick={handleAdd}
        disabled={!width || !height || parseFloat(width) <= 0 || parseFloat(height) <= 0}
        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Add Custom Window
      </button>
    </div>
  );
};

const PropertyElevationDiagram: React.FC<PropertyElevationDiagramProps> = ({
  elevationType,
  elevationId,
  elevationNumber,
  propertyData,
  wallInsulationType,
  initialPlacedElements = [],
  initialDimensions,
  floorPlanWindows = [],
  walls = [],
  roomCustomLabels = {},
  currentRooms = [],
  onElevationDataChange
}) => {
  const [selectedTool, setSelectedTool] = useState<ToolType>(null);
  // Deduplicate initial placed elements by ID to fix duplicate door/window issues
  const deduplicatedInitialElements = useMemo(() => {
    if (!initialPlacedElements || initialPlacedElements.length === 0) return [];
    
    const seen = new Set<string>();
    const deduplicated = initialPlacedElements.filter(el => {
      if (seen.has(el.id)) {
        console.warn(`🔧 [DEDUP] Removing duplicate element: ${el.id} (${el.type})`);
        return false;
      }
      seen.add(el.id);
      return true;
    });
    
    if (deduplicated.length < initialPlacedElements.length) {
      console.log(`🔧 [DEDUP] Removed ${initialPlacedElements.length - deduplicated.length} duplicate element(s)`);
    }
    
    return deduplicated;
  }, [initialPlacedElements]);
  
  const [placedElements, setPlacedElements] = useState<PlacedElement[]>(deduplicatedInitialElements);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [elevationDimensions, setElevationDimensions] = useState<ElevationDimensions>({
    height: initialDimensions?.height || 5.0,
    width: initialDimensions?.width || 5.0
  });
  
  // Window selection modal state
  const [showWindowSelector, setShowWindowSelector] = useState(false);
  const [selectedFloorPlanWindow, setSelectedFloorPlanWindow] = useState<FloorPlanWindow | null>(null);
  const [pendingWindowId, setPendingWindowId] = useState<string | null>(null); // Track newly placed window awaiting configuration

  // Door measurement modal state (fullscreen/mobile-focused)
  const [showDoorModal, setShowDoorModal] = useState(false);
  const [pendingDoorId, setPendingDoorId] = useState<string | null>(null);
  const [doorHeightInput, setDoorHeightInput] = useState<string>('2.0');
  const [doorWidthInput, setDoorWidthInput] = useState<string>('0.89');

  // Track initialization to prevent infinite loops
  const hasInitialized = useRef(false);
  const lastElevationType = useRef<string>();
  
  // Track if touch was used to prevent duplicate mouse events (touch devices trigger both)
  const touchUsedRef = useRef(false);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // One-time initialization and reset when elevation type changes
  useEffect(() => {
    // Reset initialization when elevation type changes
    if (lastElevationType.current !== elevationType) {
      hasInitialized.current = false;
      lastElevationType.current = elevationType;
    }

    // Only initialize once per elevation type, or if we have initial data and haven't initialized yet
    if (!hasInitialized.current && (deduplicatedInitialElements.length > 0 || initialDimensions)) {
      // Initialize without logging to avoid spam
      if (deduplicatedInitialElements.length > 0) {
        setPlacedElements(deduplicatedInitialElements);
      }
      
      if (initialDimensions) {
        setElevationDimensions(initialDimensions);
      }
      
      hasInitialized.current = true;
    }
  }, [elevationType, deduplicatedInitialElements, initialDimensions]); // Include deduplicatedInitialElements and initialDimensions

  // Cleanup effect to restore scroll behavior if component unmounts while dragging
  useEffect(() => {
    return () => {
      // Restore scroll behavior on cleanup
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      
      // Clear touch timeout
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, []);

  // Effect to handle escape key to cancel dragging
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDragging) {
        setIsDragging(false);
        setDragPosition(null);
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDragging]);

  // Manage body scroll when fullscreen overlay is active
  useEffect(() => {
    if (!isFullscreen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  // Effect to report data changes to parent
  useEffect(() => {
    if (onElevationDataChange) {
      const currentElements = placedElements.filter(el => el.elevation === elevationType);
      const totalWallArea = elevationDimensions.height * elevationDimensions.width;
      const totalElementsArea = currentElements.reduce((total, element) => {
        if (element.type === 'airvent') return total; // Air vents don't count for area calculation
        const height = element.height || 0;
        const width = element.width || 0;
        return total + (height * width);
      }, 0);
      const treatableArea = totalWallArea - totalElementsArea;

      // Get elements by type for detailed export
      const doors = currentElements.filter(el => el.type === 'door').map(door => ({
        id: door.id,
        height: door.height || 2.0,
        width: door.width || 0.89
      }));

      const windows = currentElements.filter(el => el.type === 'window').map(window => ({
        id: window.id,
        height: window.height || 1.0,
        width: window.width || 1.5
      }));

      const airVents = currentElements.filter(el => el.type === 'airvent').map(vent => ({
        id: vent.id
      }));

      onElevationDataChange(elevationId, {
        wallArea: totalWallArea,
        untreatableArea: totalElementsArea, // Elements area is untreatable
        treatableArea: treatableArea,
        dimensions: {
          height: elevationDimensions.height,
          width: elevationDimensions.width
        },
        elements: {
          doors,
          windows,
          airVents
        },
        placedElements: currentElements, // Include exact placement data for PDF diagram
        wallInsulationType: wallInsulationType, // Include wall insulation type for brick pattern
        buildingHeight: getBuildingHeight() // Include building height for proper diagram sizing
      });
    }
  }, [elevationId, elevationType, elevationDimensions.height, elevationDimensions.width, placedElements, onElevationDataChange, wallInsulationType]);

  if (!elevationType) {
    return null;
  }

  // Detect if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Calculate building characteristics based on property data
  const getBuildingHeight = () => {
    if (propertyData.propertyType === 'bungalow') return 1;
    
    // For all property types (including flats), use the actual floor count
    // The roof type affects whether we show a roof, not the building height
    const floorCount = propertyData.floorCount || 1;
    return Math.max(floorCount, 1);
  };

  const hasRoof = () => {
    // No roof if it's flat (F) or has dwelling/space above (S, A, NR)
    return !['F', 'S', 'A', 'NR'].includes(propertyData.roofTypeRdSAP);
  };

  const getRoofStyle = () => {
    if (!hasRoof()) return 'flat';
    if (propertyData.roofTypeRdSAP === 'PT') return 'thatch';
    if (propertyData.roofTypeRdSAP === 'PS') return 'sloping';
    return 'pitched'; // PA, PN
  };

  const hasBasement = () => {
    return ['P', 'U', 'E'].includes(propertyData.groundLevel);
  };

  const isAttached = () => {
    return ['semi-detached', 'mid-terrace', 'end-terrace', 'enclosed-mid-terrace', 'enclosed-end-terrace'].includes(propertyData.detachment);
  };

  const buildingHeight = getBuildingHeight();
  const roofStyle = getRoofStyle();
  const showBasement = hasBasement();
  const showRoof = hasRoof();
  const attached = isAttached();

  // Calculate adaptive dimensions based on story count
  const getAdaptiveDimensions = () => {
    const isSingleStory = buildingHeight === 1;
    
    if (isSingleStory) {
      // Single story: closer/fuller view with larger story height
      return {
        svgWidth: 500,
        svgHeight: 420,
        viewBoxWidth: 500,
        viewBoxHeight: 420,
        storyHeight: 204, // 120 * 1.7 = 204 (70% increase)
        buildingWidth: 350,
        buildingX: 75,
        groundY: 370
      };
    } else {
      // Multi-story: adjust to fit everything including roof
      const baseStoryHeight = buildingHeight <= 2 ? 136 : Math.max(102, 238 / buildingHeight); // 80 * 1.7 = 136, 60 * 1.7 = 102, 140 * 1.7 = 238
      const totalRequiredHeight = (buildingHeight * baseStoryHeight) + (showRoof ? 80 : 0) + (showBasement ? 40 : 0) + 80; // 80 for margins
      const adaptiveHeight = Math.max(450, totalRequiredHeight);
      
      return {
        svgWidth: 500,
        svgHeight: adaptiveHeight,
        viewBoxWidth: 500,
        viewBoxHeight: adaptiveHeight,
        storyHeight: baseStoryHeight,
        buildingWidth: 350,
        buildingX: 75,
        groundY: adaptiveHeight - 80 // Keep some margin from bottom
      };
    }
  };

  // Handle mouse down to start dragging (desktop)
  const handleDiagramMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!selectedTool || isDragging) return;
    
    // Prevent mouse events if touch was recently used (avoid double placement)
    if (touchUsedRef.current) {
      console.log('🚫 Ignoring mouse event - touch was used');
      return;
    }

    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const dimensions = getAdaptiveDimensions();
    
    // Calculate mouse position relative to the SVG viewBox
    const x = ((event.clientX - rect.left) / rect.width) * dimensions.viewBoxWidth;
    const y = ((event.clientY - rect.top) / rect.height) * dimensions.viewBoxHeight;
    
    // Allow starting drag anywhere in the SVG
    setIsDragging(true);
    setDragPosition({ x: event.clientX, y: event.clientY });
    
    // Prevent page scrolling during drag
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  };

  // Handle mouse move during drag (desktop)
  const handleDiagramMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!isDragging || !selectedTool) return;
    
    // Prevent mouse events if touch was recently used
    if (touchUsedRef.current) return;
    
    event.preventDefault();
    setDragPosition({ x: event.clientX, y: event.clientY });
  };

  // Handle mouse up to place element (desktop)
  const handleDiagramMouseUp = (event: React.MouseEvent<SVGSVGElement>) => {
    if (!isDragging || !selectedTool) return;
    
    // Prevent mouse events if touch was recently used (avoid double placement)
    if (touchUsedRef.current) {
      console.log('🚫 Ignoring mouse placement - touch was used');
      setIsDragging(false);
      setDragPosition(null);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      return;
    }

    // Eraser tool doesn't place elements, only removes them on click
    if (selectedTool === 'eraser') {
      setIsDragging(false);
      setDragPosition(null);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      return;
    }

    const svg = event.currentTarget;
    
    // Use SVG's built-in coordinate transformation for accuracy
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    
    // Transform screen coordinates to SVG coordinates
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const x = svgP.x;
    const y = svgP.y;
    
    console.log('[PropertyElevationDiagram] Mouse placement:', {
      screenX: event.clientX,
      screenY: event.clientY,
      svgX: x,
      svgY: y,
      tool: selectedTool
    });
    
    // Allow placement anywhere in the diagram (no bounds checking)
    const newElement: PlacedElement = {
      id: `${selectedTool}-${Date.now()}-${Math.random()}`,
      type: selectedTool,
      x,
      y,
      elevation: elevationType,
      height: selectedTool === 'door' ? 2.0 : selectedTool === 'window' ? 1.0 : undefined,
      width: selectedTool === 'door' ? 0.89 : selectedTool === 'window' ? 1.5 : undefined
    };
    
    setPlacedElements(prev => [...prev, newElement]);
    
    // If a window was placed, show the selection modal
    if (selectedTool === 'window') {
      setPendingWindowId(newElement.id);
      setShowWindowSelector(true);
    }

    // If a door was placed, prompt for measurements
    if (selectedTool === 'door') {
      setPendingDoorId(newElement.id);
      setDoorHeightInput((newElement.height ?? 2.0).toString());
      setDoorWidthInput((newElement.width ?? 0.89).toString());
      setShowDoorModal(true);
    }
    
    // End dragging and restore page scrolling
    setIsDragging(false);
    setDragPosition(null);
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  };

  // Handle touch start to begin dragging (touch devices)
  const handleDiagramTouchStart = (event: React.TouchEvent<SVGSVGElement>) => {
    if (!selectedTool || isDragging) return;
    
    // Mark that touch was used to prevent duplicate mouse events
    touchUsedRef.current = true;
    
    // Clear any existing timeout
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }
    
    // Reset touch flag after 500ms (enough time for mouse events to be ignored)
    touchTimeoutRef.current = setTimeout(() => {
      touchUsedRef.current = false;
    }, 500);
    
    // Don't call preventDefault - causes passive event listener error
    const touch = event.touches[0];
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const dimensions = getAdaptiveDimensions();
    
    // Calculate touch position relative to the SVG viewBox
    const x = ((touch.clientX - rect.left) / rect.width) * dimensions.viewBoxWidth;
    const y = ((touch.clientY - rect.top) / rect.height) * dimensions.viewBoxHeight;
    
    // Allow starting drag anywhere in the SVG
    setIsDragging(true);
    setDragPosition({ x: touch.clientX, y: touch.clientY });
    
    // Prevent page scrolling during drag
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  };

  // Handle touch move during drag (touch devices)
  const handleDiagramTouchMove = (event: React.TouchEvent<SVGSVGElement>) => {
    if (!isDragging || !selectedTool) return;
    
    // Don't call preventDefault - causes passive event listener error
    const touch = event.touches[0];
    setDragPosition({ x: touch.clientX, y: touch.clientY });
  };

  // Handle touch end to place element (touch devices)
  const handleDiagramTouchEnd = (event: React.TouchEvent<SVGSVGElement>) => {
    if (!isDragging || !selectedTool) return;
    
    // Eraser tool doesn't place elements, only removes them on click
    if (selectedTool === 'eraser') {
      setIsDragging(false);
      setDragPosition(null);
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      return;
    }
    
    // Don't call preventDefault - causes passive event listener error
    const touch = event.changedTouches[0];
    const svg = event.currentTarget;
    
    // Use SVG's built-in coordinate transformation for accuracy
    const pt = svg.createSVGPoint();
    pt.x = touch.clientX;
    pt.y = touch.clientY;
    
    // Transform screen coordinates to SVG coordinates
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const x = svgP.x;
    const y = svgP.y;
    
    console.log('[PropertyElevationDiagram] Touch placement:', {
      screenX: touch.clientX,
      screenY: touch.clientY,
      svgX: x,
      svgY: y,
      tool: selectedTool
    });
    
    // Allow placement anywhere in the diagram (no bounds checking)
    const newElement: PlacedElement = {
      id: `${selectedTool}-${Date.now()}-${Math.random()}`,
      type: selectedTool,
      x,
      y,
      elevation: elevationType,
      height: selectedTool === 'door' ? 2.0 : selectedTool === 'window' ? 1.0 : undefined,
      width: selectedTool === 'door' ? 0.89 : selectedTool === 'window' ? 1.5 : undefined
    };
    
    setPlacedElements(prev => [...prev, newElement]);
    
    // If a window was placed, show the selection modal
    if (selectedTool === 'window') {
      setPendingWindowId(newElement.id);
      setShowWindowSelector(true);
    }
    
    // If a door was placed via touch, prompt for measurements (mobile/fullscreen-friendly)
    if (selectedTool === 'door') {
      setPendingDoorId(newElement.id);
      setDoorHeightInput((newElement.height ?? 2.0).toString());
      setDoorWidthInput((newElement.width ?? 0.89).toString());
      setShowDoorModal(true);
    }
    
    // End dragging and restore page scrolling
    setIsDragging(false);
    setDragPosition(null);
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  };

  // Handle toolbar button interaction - Works the same for both touch and desktop
  const handleToolbarButtonClick = (tool: ToolType) => {
    // Same behavior for all tools (reverted window tool back to normal)
    if (selectedTool === tool) {
      // If clicking the already selected tool, deselect it
      setSelectedTool(null);
    } else {
      // Select the new tool
      setSelectedTool(tool);
    }
  };

  // Remove an element when clicked
  const removeElement = (elementId: string) => {
    setPlacedElements(prev => prev.filter(el => el.id !== elementId));
  };

  // Clear all elements for current elevation
  const clearAllElements = () => {
    setPlacedElements(prev => prev.filter(el => el.elevation !== elevationType));
  };

  // Configure placed window with floor plan selection
  const configureFloorPlanWindow = (floorPlanWindow: FloorPlanWindow) => {
    if (!pendingWindowId) return;
    
    setPlacedElements(prev => prev.map(element => 
      element.id === pendingWindowId 
        ? {
            ...element,
            height: floorPlanWindow.height / 1000, // Convert mm to m
            width: floorPlanWindow.width / 1000    // Convert mm to m
          }
        : element
    ));
    
    setShowWindowSelector(false);
    setSelectedFloorPlanWindow(null);
    setPendingWindowId(null);
  };

  // Configure placed window with custom dimensions
  const configureCustomWindow = (width: number, height: number) => {
    if (!pendingWindowId) return;
    
    setPlacedElements(prev => prev.map(element => 
      element.id === pendingWindowId 
        ? {
            ...element,
            height: height,
            width: width
          }
        : element
    ));
    
    setShowWindowSelector(false);
    setPendingWindowId(null);
  };

  // Comprehensive room lookup function with multiple fallback strategies
  const findRoomNameForWindow = (window: FloorPlanWindow): string => {
    // Strategy 1: If window already has roomName, use it
    if (window.roomName && window.roomName !== 'Unknown Room') {
      return window.roomName;
    }
    
    // Strategy 2: Use position-based lookup (most reliable)
    if (window.position && currentRooms.length > 0) {
      const roomAtPosition = findRoomAtPoint(window.position, currentRooms);
      if (roomAtPosition && roomAtPosition.name) {
        return roomAtPosition.name;
      }
    }
    
    // Strategy 3: Wall-based lookup (fallback)
    if (window.wallId && walls.length > 0 && roomCustomLabels) {
      const wall = walls.find((w: any) => 
        w.stableId === window.wallId || 
        w.tempId === window.wallId ||
        w.id === window.wallId
      );
      
      if (wall) {
        // Try multiple room identification approaches
        const roomId = (wall as any).originatingRoomId || 
                      (wall as any).assignedRoomIds?.[0] ||
                      (wall as any).roomId;
        
        if (roomId && roomCustomLabels[roomId]) {
          return roomCustomLabels[roomId];
        }
      }
    }
    
    // Strategy 4: Try to find room by checking all rooms for this window
    if (currentRooms.length > 0) {
      for (const room of currentRooms) {
        if (room.elements && room.elements.some((el: any) => el.id === window.id)) {
          return room.name || 'Unknown Room';
        }
      }
    }
    
    return 'Unknown Room';
  };

  // Update element dimensions
  const updateElementDimensions = (elementId: string, height: number, width: number) => {
    setPlacedElements(prev => prev.map(element => 
      element.id === elementId 
        ? { ...element, height, width }
        : element
    ));
  };

  // Calculate total wall area
  const getTotalWallArea = () => {
    return elevationDimensions.height * elevationDimensions.width;
  };

  // Calculate total elements area
  const getTotalElementsArea = () => {
    const currentElements = placedElements.filter(el => el.elevation === elevationType);
    return currentElements.reduce((total, element) => {
      if (element.type === 'airvent') return total; // Air vents don't count for area calculation
      const height = element.height || 0;
      const width = element.width || 0;
      return total + (height * width);
    }, 0);
  };

  // Calculate treatable area
  const getTreatableArea = () => {
    return getTotalWallArea() - getTotalElementsArea();
  };

  // Get elements by type for current elevation
  const getElementsByType = (type: 'window' | 'door' | 'airvent') => {
    return placedElements.filter(el => el.elevation === elevationType && el.type === type);
  };

  // Render bottom tables and calculations
  const renderBottomTables = () => {
    const doors = getElementsByType('door');
    const windows = getElementsByType('window');
    const airvents = getElementsByType('airvent');

    return (
      <div className="mt-6 space-y-4">
        {/* Elements and Calculations Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Doors Table */}
          {doors.length > 0 && (
            <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-amber-100 rounded-lg">
                  <DoorOpen className="w-4 h-4 text-amber-600" />
                </div>
                <h4 className="text-base font-semibold text-slate-800">Doors</h4>
                <span className="ml-auto bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                  {doors.length}
                </span>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-amber-50 to-amber-100">
                    <tr>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-amber-800 border-r border-amber-200">ID</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-amber-800 border-r border-amber-200">Height (m)</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-amber-800 border-r border-amber-200">Width (m)</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-amber-800">Area (m²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doors.map((door, index) => {
                      const doorId = generateElementId(door, placedElements);
                      const height = door.height || 2.0;
                      const width = door.width || 0.89;
                      const area = height * width;
                      
                      return (
                        <tr key={door.id} className="border-t border-amber-200 hover:bg-amber-50 transition-colors">
                          <td className="px-2 py-2 border-r border-amber-200 text-center">
                            <div className="inline-flex items-center justify-center w-6 h-6 bg-amber-600 text-white text-xs font-bold rounded-full">
                              {doorId}
                            </div>
                          </td>
                          <td className="px-2 py-2 border-r border-amber-200">
                            <input
                              type="number"
                              step="0.01"
                              value={height}
                              onChange={(e) => updateElementDimensions(door.id, parseFloat(e.target.value) || 0, door.width || 0.89)}
                              className="w-full px-2 py-1 border border-amber-300 rounded text-center text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            />
                          </td>
                          <td className="px-2 py-2 border-r border-amber-200">
                            <input
                              type="number"
                              step="0.01"
                              value={width}
                              onChange={(e) => updateElementDimensions(door.id, door.height || 2.0, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-amber-300 rounded text-center text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                            />
                          </td>
                          <td className="px-2 py-2 text-center text-sm font-bold text-amber-800 bg-amber-50">
                            {area.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Windows Table */}
          {windows.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-sky-100 rounded-lg">
                  <RectangleHorizontal className="w-4 h-4 text-sky-600" />
                </div>
                <h4 className="text-base font-semibold text-slate-800">Windows</h4>
                <span className="ml-auto bg-sky-100 text-sky-700 px-2 py-1 rounded-full text-xs font-medium">
                  {windows.length}
                </span>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-sky-50 to-sky-100">
                    <tr>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-sky-800 border-r border-sky-200">ID</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-sky-800 border-r border-sky-200">Height (m)</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-sky-800 border-r border-sky-200">Width (m)</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-sky-800">Area (m²)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {windows.map((window, index) => {
                      const windowId = generateElementId(window, placedElements);
                      const height = window.height || 1.0;
                      const width = window.width || 1.5;
                      const area = height * width;
                      
                      return (
                        <tr key={window.id} className="border-t border-sky-200 hover:bg-sky-50 transition-colors">
                          <td className="px-2 py-2 border-r border-sky-200 text-center">
                            <div className="inline-flex items-center justify-center w-6 h-6 bg-sky-600 text-white text-xs font-bold rounded-full">
                              {windowId}
                            </div>
                          </td>
                          <td className="px-2 py-2 border-r border-sky-200">
                            <input
                              type="number"
                              step="0.01"
                              value={height}
                              onChange={(e) => updateElementDimensions(window.id, parseFloat(e.target.value) || 0, window.width || 1.5)}
                              className="w-full px-2 py-1 border border-sky-300 rounded text-center text-xs focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
                            />
                          </td>
                          <td className="px-2 py-2 border-r border-sky-200">
                            <input
                              type="number"
                              step="0.01"
                              value={width}
                              onChange={(e) => updateElementDimensions(window.id, window.height || 1.0, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-sky-300 rounded text-center text-xs focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
                            />
                          </td>
                          <td className="px-2 py-2 text-center text-sm font-bold text-sky-800 bg-sky-50">
                            {area.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Air Vents List */}
          {airvents.length > 0 && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-emerald-100 rounded-lg">
                  <Wind className="w-4 h-4 text-emerald-600" />
                </div>
                <h4 className="text-base font-semibold text-slate-800">Air Vents</h4>
                <span className="ml-auto bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
                  {airvents.length}
                </span>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-3 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700 font-medium">
                  {airvents.length} air vent{airvents.length !== 1 ? 's' : ''} placed
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Air vents do not affect area calculations
                </p>
              </div>
            </div>
          )}

          {/* Area Calculations */}
          <div className="bg-white rounded-lg p-2 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1 bg-purple-100 rounded">
                <Calculator className="w-3 h-3 text-purple-600" />
              </div>
              <h4 className="text-sm font-semibold text-slate-800">Calculations</h4>
            </div>
            
            <div className="space-y-1.5">
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-xs font-medium text-slate-700">Wall Area</span>
                </div>
                <span className="text-xs font-semibold text-blue-700">{getTotalWallArea().toFixed(2)}m²</span>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-200">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                  <span className="text-xs font-medium text-slate-700">Window/Door</span>
                </div>
                <span className="text-xs font-semibold text-red-700">{getTotalElementsArea().toFixed(2)}m²</span>
              </div>
              
              <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                <div className="flex items-center gap-1">
                  <Target className="w-3 h-3 text-green-600" />
                  <span className="text-xs font-semibold text-slate-800">Treatable</span>
                </div>
                <span className="text-sm font-bold text-green-700">{getTreatableArea().toFixed(2)}m²</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render drag ghost element
  const renderDragGhost = () => {
    if (!isDragging || !selectedTool || !dragPosition) return null;

    // Calculate proper offset to center ghost under cursor
    // Ghost sizes: window=40x32px (w-10 h-8), door=40x80px (w-10 h-20), airvent=12x6px (w-3 h-1.5)
    let offsetX, offsetY;
    switch (selectedTool) {
      case 'window':
        offsetX = 20; // Half of 40px width (w-10 = 2.5rem = 40px)
        offsetY = 16; // Half of 32px height (h-8 = 2rem = 32px)
        break;
      case 'door':
        offsetX = 20; // Half of 40px width
        offsetY = 40; // Half of 80px height (h-20)
        break;
      case 'airvent':
        offsetX = 6; // Half of 12px width (w-3)
        offsetY = 3; // Half of 6px height (h-1.5)
        break;
      default:
        offsetX = 20;
        offsetY = 16;
    }

    const ghostStyle: React.CSSProperties = {
      position: 'fixed',
      left: dragPosition.x - offsetX,
      top: dragPosition.y - offsetY,
      pointerEvents: 'none',
      zIndex: 9999,
      opacity: 0.7,
    };

    let ghostElement: JSX.Element | null = null;

    switch (selectedTool) {
      case 'window':
        ghostElement = (
          <div style={ghostStyle}>
            <div className="relative w-10 h-8 bg-sky-300 border-2 border-blue-600 rounded shadow-lg">
              <div className="absolute inset-1 border border-blue-600 rounded-sm">
                <div className="absolute top-1/2 left-0 right-0 h-px bg-blue-600 transform -translate-y-1/2"></div>
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-blue-600 transform -translate-x-1/2"></div>
              </div>
              <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-600 text-white text-xs flex items-center justify-center rounded-full font-bold opacity-80">
                W
              </div>
            </div>
          </div>
        );
        break;
      case 'door':
        ghostElement = (
          <div style={ghostStyle}>
            <div className="relative w-10 h-20 bg-amber-600 border-2 border-amber-800 rounded shadow-lg">
              <div className="absolute inset-1 space-y-1">
                <div className="h-6 border border-amber-800 rounded-sm"></div>
                <div className="h-6 border border-amber-800 rounded-sm"></div>
              </div>
              <div className="absolute right-1 top-8 w-1 h-1 bg-amber-800 rounded-full"></div>
              <div className="absolute -top-1 -left-1 w-3 h-3 bg-amber-600 text-white text-xs flex items-center justify-center rounded-full font-bold opacity-80">
                D
              </div>
            </div>
          </div>
        );
        break;
      case 'airvent':
        ghostElement = (
          <div style={ghostStyle}>
            <div className="relative w-3 h-1.5 bg-gray-700 border border-gray-900 rounded-sm shadow-sm">
              <div className="absolute inset-0 flex flex-col justify-center">
                <div className="h-px bg-gray-900 mx-px"></div>
                <div className="h-px bg-gray-900 mx-px"></div>
              </div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-gray-700 text-white text-xs flex items-center justify-center rounded-full font-bold opacity-80" style={{ fontSize: '6px' }}>
                V
              </div>
            </div>
          </div>
        );
        break;
      default:
        break;
    }

    // Render ghost using portal to avoid CSS transform issues from parent containers
    return ghostElement ? ReactDOM.createPortal(ghostElement, document.body) : null;
  };

  // Generate element IDs based on type and order
  const generateElementId = (element: any, elements: any[]) => {
    const sameTypeElements = elements
      .filter(el => el.type === element.type && el.elevation === elevationType)
      .sort((a, b) => a.id.localeCompare(b.id)); // Sort by original ID for consistency
    
    const index = sameTypeElements.findIndex(el => el.id === element.id) + 1;
    
    switch (element.type) {
      case 'door':
        return `D${index}`;
      case 'window':
        return `W${index}`;
      case 'airvent':
        return `V${index}`;
      default:
        return `${index}`;
    }
  };

  // Render placed elements with enhanced visuals and ID labels
  const renderPlacedElements = () => {
    const currentElements = placedElements.filter(element => element.elevation === elevationType);
    
    return currentElements.map(element => {
      const elementId = generateElementId(element, placedElements);
      
      switch (element.type) {
        case 'window':
          return (
            <g key={element.id}>
              {/* Enhanced window with frame and glass effect */}
              <rect 
                x={element.x - 20} 
                y={element.y - 15} 
                width={40} 
                height={30} 
                fill="rgba(135, 206, 250, 0.8)" 
                stroke="#1e40af" 
                strokeWidth="2"
                rx="2"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeElement(element.id);
                }}
              />
              {/* Window frame cross */}
              <line 
                x1={element.x} 
                y1={element.y - 15} 
                x2={element.x} 
                y2={element.y + 15} 
                stroke="#1e40af" 
                strokeWidth="1"
              />
              <line 
                x1={element.x - 20} 
                y1={element.y} 
                x2={element.x + 20} 
                y2={element.y} 
                stroke="#1e40af" 
                strokeWidth="1"
              />
              {/* Window ID label */}
              <circle 
                cx={element.x - 15} 
                cy={element.y - 10} 
                r="8" 
                fill="#1e40af" 
                stroke="white" 
                strokeWidth="1"
              />
              <text 
                x={element.x - 15} 
                y={element.y - 6} 
                textAnchor="middle" 
                fontSize="10" 
                fill="white" 
                fontWeight="bold"
              >
                {elementId}
              </text>
            </g>
          );
        case 'door':
          return (
            <g key={element.id}>
              {/* Enhanced door with panel details */}
              <rect 
                x={element.x - 20} 
                y={element.y - 40} 
                width={40} 
                height={80} 
                fill="#d97706" 
                stroke="#92400e" 
                strokeWidth="2"
                rx="2"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeElement(element.id);
                }}
              />
              {/* Door panels */}
              <rect 
                x={element.x - 16} 
                y={element.y - 35} 
                width={32} 
                height={30} 
                fill="none" 
                stroke="#92400e" 
                strokeWidth="1"
                rx="1"
              />
              <rect 
                x={element.x - 16} 
                y={element.y} 
                width={32} 
                height={30} 
                fill="none" 
                stroke="#92400e" 
                strokeWidth="1"
                rx="1"
              />
              {/* Door handle */}
              <circle 
                cx={element.x + 12} 
                cy={element.y - 5} 
                r="2" 
                fill="#92400e"
              />
              {/* Door ID label */}
              <circle 
                cx={element.x - 15} 
                cy={element.y - 30} 
                r="8" 
                fill="#d97706" 
                stroke="white" 
                strokeWidth="1"
              />
              <text 
                x={element.x - 15} 
                y={element.y - 26} 
                textAnchor="middle" 
                fontSize="10" 
                fill="white" 
                fontWeight="bold"
              >
                {elementId}
              </text>
            </g>
          );
        case 'airvent':
          return (
            <g key={element.id}>
              {/* Enhanced air vent with grill pattern */}
              <rect 
                x={element.x - 6} 
                y={element.y - 3} 
                width={12} 
                height={6} 
                fill="#374151" 
                stroke="#111827" 
                strokeWidth="1"
                rx="1"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeElement(element.id);
                }}
              />
              {/* Vent grill lines */}
              <line x1={element.x - 4} y1={element.y - 1} x2={element.x + 4} y2={element.y - 1} stroke="#111827" strokeWidth="0.5"/>
              <line x1={element.x - 4} y1={element.y + 1} x2={element.x + 4} y2={element.y + 1} stroke="#111827" strokeWidth="0.5"/>
              {/* Vent ID label */}
              <circle 
                cx={element.x + 8} 
                cy={element.y - 5} 
                r="6" 
                fill="#374151" 
                stroke="white" 
                strokeWidth="1"
              />
              <text 
                x={element.x + 8} 
                y={element.y - 2} 
                textAnchor="middle" 
                fontSize="8" 
                fill="white" 
                fontWeight="bold"
              >
                {elementId}
              </text>
            </g>
          );
        default:
          return null;
      }
    });
  };

  // Render cavity brickwork pattern when cavity insulation is selected
  const renderCavityBrickwork = (x: number, y: number, width: number, height: number) => {
    // Show cavity brickwork only for Cavity wall type
    if (wallInsulationType !== 'cavity') return null;

    const brickHeight = 8;
    const brickWidth = 24;
    const mortarThickness = 2;
    const rows = Math.floor(height / (brickHeight + mortarThickness));
    const cols = Math.floor(width / (brickWidth + mortarThickness));

    return (
      <g>
        {/* Simplified brown brickwork pattern across full width */}
        {Array.from({ length: rows }, (_, row) => (
          <g key={`brick-row-${row}`}>
            {Array.from({ length: cols }, (_, col) => {
              const brickX = x + col * (brickWidth + mortarThickness) + (row % 2) * (brickWidth / 2);
              const brickY = y + row * (brickHeight + mortarThickness);
              
              if (brickX + brickWidth <= x + width && brickY + brickHeight <= y + height) {
                return (
                  <rect
                    key={`brick-${row}-${col}`}
                    x={brickX}
                    y={brickY}
                    width={brickWidth}
                    height={brickHeight}
                    fill="white"
                    stroke="#8B4513"
                    strokeWidth="0.5"
                  />
                );
              }
              return null;
            })}
          </g>
        ))}
      </g>
    );
  };

  const renderFrontElevation = () => {
    const dimensions = getAdaptiveDimensions();
    const { storyHeight, buildingWidth, buildingX, groundY, svgWidth, svgHeight, viewBoxWidth, viewBoxHeight } = dimensions;
    const basementHeight = showBasement ? 40 : 0;
    const buildingY = groundY - (buildingHeight * storyHeight) - basementHeight;
    const dpcHeight = 20; // DPC section height

    return (
      <svg 
        width={svgWidth} 
        height={svgHeight} 
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} 
        className="border border-gray-300 rounded"
        onMouseDown={handleDiagramMouseDown}
        onMouseMove={handleDiagramMouseMove}
        onMouseUp={handleDiagramMouseUp}
        onTouchStart={handleDiagramTouchStart}
        onTouchMove={handleDiagramTouchMove}
        onTouchEnd={handleDiagramTouchEnd}
        style={{ 
          cursor: selectedTool && !isTouchDevice ? 
            (selectedTool === 'eraser' ? 'pointer' : (isDragging ? 'grabbing' : 'crosshair')) : 
            'default',
          touchAction: 'none'
        }}
      >
        {/* Ground */}
        <line x1="0" y1={groundY} x2={viewBoxWidth} y2={groundY} stroke="black" strokeWidth="2"/>
        
        {/* Basement/Foundation if applicable */}
        {showBasement && (
          <rect 
            x={buildingX} 
            y={groundY - basementHeight} 
            width={buildingWidth} 
            height={basementHeight} 
            fill="white" 
            stroke="black" 
            strokeWidth="1"
          />
        )}
        
        {/* Main building structure - dynamic height */}
        <rect 
          x={buildingX} 
          y={buildingY} 
          width={buildingWidth} 
          height={buildingHeight * storyHeight} 
          fill="white" 
          stroke="black" 
          strokeWidth="2"
        />
        
        {/* DPC Section - bottom band without brickwork for air vents */}
        <rect 
          x={buildingX} 
          y={buildingY + (buildingHeight * storyHeight) - dpcHeight} 
          width={buildingWidth} 
          height={dpcHeight} 
          fill="#f5f5f5" 
          stroke="black" 
          strokeWidth="1"
        />
        
        {/* Cavity brickwork pattern - excluding DPC section */}
        {renderCavityBrickwork(buildingX, buildingY, buildingWidth, (buildingHeight * storyHeight) - dpcHeight)}
        
        {/* Floor division lines for multi-storey */}
        {Array.from({ length: buildingHeight - 1 }, (_, i) => (
          <line 
            key={i}
            x1={buildingX} 
            y1={buildingY + ((i + 1) * storyHeight)} 
            x2={buildingX + buildingWidth} 
            y2={buildingY + ((i + 1) * storyHeight)} 
            stroke="black" 
            strokeWidth="3"
          />
        ))}
        
        {/* Roof - conditional based on roof type */}
        {showRoof && roofStyle === 'pitched' && (
          <>
            <polygon 
              points={`${buildingX},${buildingY} ${buildingX + buildingWidth/2},${buildingY - 60} ${buildingX + buildingWidth},${buildingY}`}
              fill="none" 
              stroke="black" 
              strokeWidth="2"
            />
            <line x1={buildingX + buildingWidth/2} y1={buildingY - 60} x2={buildingX + buildingWidth/2} y2={buildingY} stroke="black" strokeWidth="1"/>
          </>
        )}
        
        {showRoof && roofStyle === 'flat' && (
          <>
            {/* Main flat roof structure */}
            <rect 
              x={buildingX} 
              y={buildingY - 15} 
              width={buildingWidth} 
              height={15} 
              fill="#d3d3d3" 
              stroke="black" 
              strokeWidth="2"
            />
            {/* Parapet walls */}
            <rect 
              x={buildingX - 5} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
            <rect 
              x={buildingX + buildingWidth} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
            {/* Drainage outlets */}
            <circle 
              cx={buildingX + buildingWidth * 0.25} 
              cy={buildingY - 7} 
              r="3" 
              fill="black"
            />
            <circle 
              cx={buildingX + buildingWidth * 0.75} 
              cy={buildingY - 7} 
              r="3" 
              fill="black"
            />
          </>
        )}
        
        {/* Render placed elements */}
        {renderPlacedElements()}
        
        {/* No title needed - elevation type is shown in dropdown */}
      </svg>
    );
  };

  const renderRightElevation = () => {
    const dimensions = getAdaptiveDimensions();
    const { storyHeight, buildingWidth, buildingX, groundY, svgWidth, svgHeight, viewBoxWidth, viewBoxHeight } = dimensions;
    const basementHeight = showBasement ? 40 : 0;
    const buildingY = groundY - (buildingHeight * storyHeight) - basementHeight;
    const dpcHeight = 20; // DPC section height
    
    return (
      <svg 
        width={svgWidth} 
        height={svgHeight} 
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} 
        className="border border-gray-300 rounded"
        onMouseDown={handleDiagramMouseDown}
        onMouseMove={handleDiagramMouseMove}
        onMouseUp={handleDiagramMouseUp}
        onTouchStart={handleDiagramTouchStart}
        onTouchMove={handleDiagramTouchMove}
        onTouchEnd={handleDiagramTouchEnd}
        style={{ 
          cursor: selectedTool && !isTouchDevice ? 
            (selectedTool === 'eraser' ? 'pointer' : (isDragging ? 'grabbing' : 'crosshair')) : 
            'default',
          touchAction: 'none'
        }}
      >
        {/* Ground */}
        <line x1="0" y1={groundY} x2={viewBoxWidth} y2={groundY} stroke="black" strokeWidth="2"/>
        
        {/* Basement/Foundation if applicable */}
        {showBasement && (
          <rect 
            x={buildingX} 
            y={groundY - basementHeight} 
            width={buildingWidth} 
            height={basementHeight} 
            fill="white" 
            stroke="black" 
            strokeWidth="1"
          />
        )}
        
        {/* Main building structure - dynamic height */}
        <rect 
          x={buildingX} 
          y={buildingY} 
          width={buildingWidth} 
          height={buildingHeight * storyHeight} 
          fill="white" 
          stroke="black" 
          strokeWidth="2"
        />
        
        {/* DPC Section - bottom band without brickwork for air vents */}
        <rect 
          x={buildingX} 
          y={buildingY + (buildingHeight * storyHeight) - dpcHeight} 
          width={buildingWidth} 
          height={dpcHeight} 
          fill="#f5f5f5" 
          stroke="black" 
          strokeWidth="1"
        />
        
        {/* Cavity brickwork pattern - excluding DPC section */}
        {renderCavityBrickwork(buildingX, buildingY, buildingWidth, (buildingHeight * storyHeight) - dpcHeight)}
        
        {/* Floor division lines for multi-storey */}
        {Array.from({ length: buildingHeight - 1 }, (_, i) => (
          <line 
            key={i}
            x1={buildingX} 
            y1={buildingY + ((i + 1) * storyHeight)} 
            x2={buildingX + buildingWidth} 
            y2={buildingY + ((i + 1) * storyHeight)} 
            stroke="black" 
            strokeWidth="3"
          />
        ))}
        
        {/* Roof - side view conditional */}
        {showRoof && roofStyle === 'pitched' && (
          <>
            {/* Pitched roof - triangular from side view */}
            <polygon 
              points={`${buildingX},${buildingY} ${buildingX + buildingWidth/2},${buildingY - 60} ${buildingX + buildingWidth},${buildingY}`}
              fill="none" 
              stroke="black" 
              strokeWidth="2"
            />
            {/* Ridge line */}
            <line 
              x1={buildingX + buildingWidth/2} 
              y1={buildingY - 60} 
              x2={buildingX + buildingWidth/2} 
              y2={buildingY} 
              stroke="black" 
              strokeWidth="1"
            />
          </>
        )}
        
        {showRoof && roofStyle === 'flat' && (
          <>
            {/* Main flat roof structure */}
            <rect 
              x={buildingX} 
              y={buildingY - 15} 
              width={buildingWidth} 
              height={15} 
              fill="#d3d3d3" 
              stroke="black" 
              strokeWidth="2"
            />
            {/* Parapet walls */}
            <rect 
              x={buildingX - 5} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
            <rect 
              x={buildingX + buildingWidth} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
          </>
        )}
        
        {/* Render placed elements */}
        {renderPlacedElements()}
        
        {/* No title needed - elevation type is shown in dropdown */}
      </svg>
    );
  };

  const renderLeftElevation = () => {
    const dimensions = getAdaptiveDimensions();
    const { storyHeight, buildingWidth, buildingX, groundY, svgWidth, svgHeight, viewBoxWidth, viewBoxHeight } = dimensions;
    const basementHeight = showBasement ? 40 : 0;
    const buildingY = groundY - (buildingHeight * storyHeight) - basementHeight;
    const dpcHeight = 20; // DPC section height
    
    return (
      <svg 
        width={svgWidth} 
        height={svgHeight} 
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} 
        className="border border-gray-300 rounded"
        onMouseDown={handleDiagramMouseDown}
        onMouseMove={handleDiagramMouseMove}
        onMouseUp={handleDiagramMouseUp}
        onTouchStart={handleDiagramTouchStart}
        onTouchMove={handleDiagramTouchMove}
        onTouchEnd={handleDiagramTouchEnd}
        style={{ 
          cursor: selectedTool && !isTouchDevice ? 
            (selectedTool === 'eraser' ? 'pointer' : (isDragging ? 'grabbing' : 'crosshair')) : 
            'default',
          touchAction: 'none'
        }}
      >
        {/* Ground */}
        <line x1="0" y1={groundY} x2={viewBoxWidth} y2={groundY} stroke="black" strokeWidth="2"/>
        
        {/* Basement/Foundation if applicable */}
        {showBasement && (
          <rect 
            x={buildingX} 
            y={groundY - basementHeight} 
            width={buildingWidth} 
            height={basementHeight} 
            fill="white" 
            stroke="black" 
            strokeWidth="1"
          />
        )}
        
        {/* Main building structure - dynamic height */}
        <rect 
          x={buildingX} 
          y={buildingY} 
          width={buildingWidth} 
          height={buildingHeight * storyHeight} 
          fill="white" 
          stroke="black" 
          strokeWidth="2"
        />
        
        {/* DPC Section - bottom band without brickwork for air vents */}
        <rect 
          x={buildingX} 
          y={buildingY + (buildingHeight * storyHeight) - dpcHeight} 
          width={buildingWidth} 
          height={dpcHeight} 
          fill="#f5f5f5" 
          stroke="black" 
          strokeWidth="1"
        />
        
        {/* Cavity brickwork pattern - excluding DPC section */}
        {renderCavityBrickwork(buildingX, buildingY, buildingWidth, (buildingHeight * storyHeight) - dpcHeight)}
        
        {/* Floor division lines for multi-storey */}
        {Array.from({ length: buildingHeight - 1 }, (_, i) => (
          <line 
            key={i}
            x1={buildingX} 
            y1={buildingY + ((i + 1) * storyHeight)} 
            x2={buildingX + buildingWidth} 
            y2={buildingY + ((i + 1) * storyHeight)} 
            stroke="black" 
            strokeWidth="3"
          />
        ))}
        
        {/* Roof - side view conditional */}
        {showRoof && roofStyle === 'pitched' && (
          <>
            {/* Pitched roof - triangular from side view */}
            <polygon 
              points={`${buildingX},${buildingY} ${buildingX + buildingWidth/2},${buildingY - 60} ${buildingX + buildingWidth},${buildingY}`}
              fill="none" 
              stroke="black" 
              strokeWidth="2"
            />
            {/* Ridge line */}
            <line 
              x1={buildingX + buildingWidth/2} 
              y1={buildingY - 60} 
              x2={buildingX + buildingWidth/2} 
              y2={buildingY} 
              stroke="black" 
              strokeWidth="1"
            />
          </>
        )}
        
        {showRoof && roofStyle === 'flat' && (
          <>
            {/* Main flat roof structure */}
            <rect 
              x={buildingX} 
              y={buildingY - 15} 
              width={buildingWidth} 
              height={15} 
              fill="#d3d3d3" 
              stroke="black" 
              strokeWidth="2"
            />
            {/* Parapet walls */}
            <rect 
              x={buildingX - 5} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
            <rect 
              x={buildingX + buildingWidth} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
          </>
        )}
        
        {/* Render placed elements */}
        {renderPlacedElements()}
        
        {/* No title needed - elevation type is shown in dropdown */}
      </svg>
    );
  };

  const renderRearElevation = () => {
    const dimensions = getAdaptiveDimensions();
    const { storyHeight, buildingWidth, buildingX, groundY, svgWidth, svgHeight, viewBoxWidth, viewBoxHeight } = dimensions;
    const basementHeight = showBasement ? 40 : 0;
    const buildingY = groundY - (buildingHeight * storyHeight) - basementHeight;
    const dpcHeight = 20; // DPC section height
    
    return (
      <svg 
        width={svgWidth} 
        height={svgHeight} 
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} 
        className="border border-gray-300 rounded"
        onMouseDown={handleDiagramMouseDown}
        onMouseMove={handleDiagramMouseMove}
        onMouseUp={handleDiagramMouseUp}
        onTouchStart={handleDiagramTouchStart}
        onTouchMove={handleDiagramTouchMove}
        onTouchEnd={handleDiagramTouchEnd}
        style={{ 
          cursor: selectedTool && !isTouchDevice ? 
            (selectedTool === 'eraser' ? 'pointer' : (isDragging ? 'grabbing' : 'crosshair')) : 
            'default',
          touchAction: 'none'
        }}
      >
        {/* Ground */}
        <line x1="0" y1={groundY} x2={viewBoxWidth} y2={groundY} stroke="black" strokeWidth="2"/>
        
        {/* Basement/Foundation if applicable */}
        {showBasement && (
          <rect 
            x={buildingX} 
            y={groundY - basementHeight} 
            width={buildingWidth} 
            height={basementHeight} 
            fill="white" 
            stroke="black" 
            strokeWidth="1"
          />
        )}
        
        {/* Main building structure - dynamic height */}
        <rect 
          x={buildingX} 
          y={buildingY} 
          width={buildingWidth} 
          height={buildingHeight * storyHeight} 
          fill="white" 
          stroke="black" 
          strokeWidth="2"
        />
        
        {/* DPC Section - bottom band without brickwork for air vents */}
        <rect 
          x={buildingX} 
          y={buildingY + (buildingHeight * storyHeight) - dpcHeight} 
          width={buildingWidth} 
          height={dpcHeight} 
          fill="#f5f5f5" 
          stroke="black" 
          strokeWidth="1"
        />
        
        {/* Cavity brickwork pattern - excluding DPC section */}
        {renderCavityBrickwork(buildingX, buildingY, buildingWidth, (buildingHeight * storyHeight) - dpcHeight)}
        
        {/* Floor division lines for multi-storey */}
        {Array.from({ length: buildingHeight - 1 }, (_, i) => (
          <line 
            key={i}
            x1={buildingX} 
            y1={buildingY + ((i + 1) * storyHeight)} 
            x2={buildingX + buildingWidth} 
            y2={buildingY + ((i + 1) * storyHeight)} 
            stroke="black" 
            strokeWidth="3"
          />
        ))}
        
        {/* Roof - conditional based on roof type */}
        {showRoof && roofStyle === 'pitched' && (
          <>
            <polygon 
              points={`${buildingX},${buildingY} ${buildingX + buildingWidth/2},${buildingY - 60} ${buildingX + buildingWidth},${buildingY}`}
              fill="none" 
              stroke="black" 
              strokeWidth="2"
            />
            <line x1={buildingX + buildingWidth/2} y1={buildingY - 60} x2={buildingX + buildingWidth/2} y2={buildingY} stroke="black" strokeWidth="1"/>
          </>
        )}
        
        {showRoof && roofStyle === 'flat' && (
          <>
            {/* Main flat roof structure */}
            <rect 
              x={buildingX} 
              y={buildingY - 15} 
              width={buildingWidth} 
              height={15} 
              fill="#d3d3d3" 
              stroke="black" 
              strokeWidth="2"
            />
            {/* Parapet walls */}
            <rect 
              x={buildingX - 5} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
            <rect 
              x={buildingX + buildingWidth} 
              y={buildingY - 25} 
              width={5} 
              height={25} 
              fill="white" 
              stroke="black" 
              strokeWidth="1"
            />
            {/* Drainage outlets */}
            <circle 
              cx={buildingX + buildingWidth * 0.25} 
              cy={buildingY - 7} 
              r="3" 
              fill="black"
            />
            <circle 
              cx={buildingX + buildingWidth * 0.75} 
              cy={buildingY - 7} 
              r="3" 
              fill="black"
            />
          </>
        )}
        
        {/* Render placed elements */}
        {renderPlacedElements()}
        
        {/* No title needed - elevation type is shown in dropdown */}
      </svg>
    );
  };

  const renderDiagram = () => {
    switch (elevationType) {
      case 'front':
        return renderFrontElevation();
      case 'right':
        return renderRightElevation();
      case 'left':
        return renderLeftElevation();
      case 'rear':
        return renderRearElevation();
      default:
        return null;
    }
  };

  const currentElevationElements = placedElements.filter(el => el.elevation === elevationType);

  return (
    <div className="mt-4 p-2 sm:p-4 bg-white rounded-lg border-2 border-blue-200 overflow-x-hidden lg:overflow-x-auto">
      {/* Mobile Warning Banner */}
      <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-500 rounded-lg lg:hidden">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-amber-800 mb-1">Mobile Device Detected</h4>
            <p className="text-xs text-amber-700">For the best experience on phones, tap the Fullscreen diagram button below to place windows, doors and vents. You can still edit the measurements and review the calculations without needing to scroll sideways.</p>
          </div>
        </div>
      </div>
      {/* Mobile fullscreen toggle */}
      <div className="flex justify-end mb-2 lg:hidden">
        <button
          type="button"
          onClick={() => setIsFullscreen(true)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200"
        >
          <Maximize2 className="w-3 h-3" />
          <span>Fullscreen diagram</span>
        </button>
      </div>
      
      <div className="flex flex-col min-w-0 lg:min-w-[600px]">
        {/* Main Content Area - no redundant title needed since dropdown shows elevation type */}
        <div className="flex flex-col">
          {/* Top Section - Toolbar and Diagram */}
          <div className="flex flex-col lg:flex-row">
            {/* Left Sidebar - Tools and Dimensions */}
            <div className="w-full lg:w-64 bg-gradient-to-b from-white to-slate-50 border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-row lg:flex-col py-2 lg:py-4 px-3 space-x-2 lg:space-x-0 lg:space-y-4 flex-shrink-0 mb-4 lg:mb-0 lg:mr-4 rounded-lg shadow-sm overflow-x-auto lg:overflow-x-visible">
              {/* Tools Section (larger screens only, tools are available in fullscreen on mobile) */}
              <div className="flex-shrink-0 hidden lg:block">
                <h4 className="text-sm font-semibold text-slate-700 mb-2 lg:mb-3 px-1">Tools</h4>
                <div className="flex flex-row lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2">
                  <button
                    type="button"
                    onClick={() => handleToolbarButtonClick('window')}
                    className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-3 p-2 lg:p-3 rounded-lg transition-colors whitespace-nowrap ${
                      selectedTool === 'window'
                        ? 'bg-blue-100 text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Window"
                  >
                    <RectangleHorizontal className="w-5 h-5" />
                    <span className="text-xs lg:text-sm font-medium">Window</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleToolbarButtonClick('door')}
                    className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-3 p-2 lg:p-3 rounded-lg transition-colors whitespace-nowrap ${
                      selectedTool === 'door'
                        ? 'bg-blue-100 text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Door"
                  >
                    <DoorOpen className="w-5 h-5" />
                    <span className="text-xs lg:text-sm font-medium">Door</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleToolbarButtonClick('airvent')}
                    className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-3 p-2 lg:p-3 rounded-lg transition-colors whitespace-nowrap ${
                      selectedTool === 'airvent'
                        ? 'bg-blue-100 text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Air Vent"
                  >
                    <Wind className="w-5 h-5" />
                    <span className="text-xs lg:text-sm font-medium">Air Vent</span>
                  </button>
                  
                  <div className="w-px lg:w-auto lg:h-px bg-gray-300 mx-1 lg:mx-0 lg:my-2"></div>
                  
                  <button
                    type="button"
                    onClick={() => handleToolbarButtonClick('eraser')}
                    className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-3 p-2 lg:p-3 rounded-lg transition-colors whitespace-nowrap ${
                      selectedTool === 'eraser'
                        ? 'bg-red-100 text-red-600 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Eraser - Click elements to remove them"
                  >
                    <Eraser className="w-5 h-5" />
                    <span className="text-xs lg:text-sm font-medium">Eraser</span>
                  </button>
                  
                  {/* Clear all button - only show if there are elements */}
                  {currentElevationElements.length > 0 && (
                    <>
                      <div className="w-px lg:w-auto lg:h-px bg-gray-300 mx-1 lg:mx-0 lg:my-2"></div>
                      <button
                        type="button"
                        onClick={clearAllElements}
                        className="flex flex-col lg:flex-row items-center gap-1 lg:gap-3 p-2 lg:p-3 rounded-lg transition-colors text-red-600 hover:bg-red-50 whitespace-nowrap"
                        title="Clear All Elements"
                      >
                        <Trash2 className="w-5 h-5" />
                        <span className="text-xs lg:text-sm font-medium">Clear All</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Dimensions Section */}
              <div className="border-l lg:border-l-0 lg:border-t border-gray-200 pl-2 lg:pl-0 lg:pt-4 flex-shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 bg-blue-100 rounded">
                    <Ruler className="w-4 h-4 text-blue-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-700">Dimensions</h4>
                </div>
                
                <div className="space-y-3">
                  <div className="group">
                    <label className="text-sm font-medium text-slate-700 block mb-1">Height</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={elevationDimensions.height}
                        onChange={(e) => setElevationDimensions(prev => ({ ...prev, height: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                      <span className="absolute right-3 top-2 text-sm text-slate-500">m</span>
                    </div>
                  </div>
                  
                  <div className="group">
                    <label className="text-sm font-medium text-slate-700 block mb-1">Width</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={elevationDimensions.width}
                        onChange={(e) => setElevationDimensions(prev => ({ ...prev, width: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                      <span className="absolute right-3 top-2 text-sm text-slate-500">m</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Diagram Area (hidden on small devices below 700px, available via fullscreen) */}
            <div className="flex-1 items-center hidden lg:flex lg:flex-col">
              {renderDiagram()}
              {renderDragGhost()}
              <div className="mt-3 text-center">
                <p className="text-sm text-black">
                  {elevationType.charAt(0).toUpperCase() + elevationType.slice(1)} view of the property
                </p>
                {selectedTool && (
                  <p className={`text-xs mt-1 ${selectedTool === 'eraser' ? 'text-red-600' : 'text-blue-600'}`}>
                    <span className="font-medium">
                      {selectedTool === 'airvent' ? 'Air Vent' : 
                       selectedTool === 'eraser' ? 'Eraser' :
                       selectedTool.charAt(0).toUpperCase() + selectedTool.slice(1)} tool selected.
                    </span> 
                    {selectedTool === 'eraser' 
                      ? ` ${isTouchDevice ? 'Tap' : 'Click'} any element on the diagram to remove it.`
                      : isDragging 
                        ? `Drag to position, then release to place.${!isTouchDevice ? ' Press Escape to cancel.' : ''}`
                        : `${isTouchDevice ? 'Touch and drag' : 'Click and drag'} anywhere on the diagram to place elements.`
                    }
                  </p>
                )}
                {!selectedTool && (
                  <p className="text-xs text-gray-600 mt-1">
                    {isTouchDevice ? 'Tap' : 'Click'} a tool button to select it, then {isTouchDevice ? 'touch and drag' : 'click and drag'} anywhere on the diagram to place elements
                  </p>
                )}
                {currentElevationElements.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {isTouchDevice ? 'Tap' : 'Click'} placed elements to remove them
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Section - Tables and Calculations */}
          {renderBottomTables()}
        </div>
        
      </div>

      {isFullscreen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/95 flex items-center justify-center">
          <div className="relative w-full max-w-[900px] h-full max-h-[700px] mx-4">
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-100 bg-slate-800 hover:bg-slate-700 border border-slate-600"
              >
                <Minimize2 className="w-3 h-3" />
                <span>Exit fullscreen</span>
              </button>
            </div>

            <div className="mt-8 bg-white rounded-xl shadow-2xl p-3 sm:p-4 h-[calc(100%-3rem)] flex flex-col">
              {/* Header with basic measurements */}
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-medium text-slate-700">
                  {elevationType.charAt(0).toUpperCase() + elevationType.slice(1)} elevation
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-600">
                  <span>H: {elevationDimensions.height.toFixed(2)}m</span>
                  <span>W: {elevationDimensions.width.toFixed(2)}m</span>
                </div>
              </div>

              {/* Compact toolbar for fullscreen */}
              <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
                {[
                  { key: 'window', label: 'Window', icon: RectangleHorizontal },
                  { key: 'door', label: 'Door', icon: DoorOpen },
                  { key: 'airvent', label: 'Air Vent', icon: Wind },
                  { key: 'eraser', label: 'Eraser', icon: Eraser },
                ].map((tool) => {
                  const Icon = tool.icon;
                  const active = selectedTool === (tool.key as ToolType);
                  const isEraser = tool.key === 'eraser';

                  return (
                    <button
                      key={tool.key}
                      type="button"
                      onClick={() => handleToolbarButtonClick(tool.key as ToolType)}
                      className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] whitespace-nowrap ${
                        active
                          ? isEraser
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{tool.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Diagram area */}
              <div className="flex-1 flex flex-col items-center justify-center overflow-hidden">
                {renderDiagram()}
                {renderDragGhost()}
              </div>

              {/* Quick area summary */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                <div className="bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
                  <div className="text-[10px] text-slate-600">Wall area</div>
                  <div className="text-xs font-semibold text-blue-800">{getTotalWallArea().toFixed(2)}m²</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                  <div className="text-[10px] text-slate-600">Windows/doors</div>
                  <div className="text-xs font-semibold text-red-800">{getTotalElementsArea().toFixed(2)}m²</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-md px-2 py-1.5">
                  <div className="text-[10px] text-slate-600">Treatable</div>
                  <div className="text-xs font-semibold text-green-800">{getTreatableArea().toFixed(2)}m²</div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Door Measurement Modal (shown after placing door in fullscreen) */}
      {showDoorModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10002]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <DoorOpen className="w-5 h-5 text-amber-600" />
                Configure Door on {elevationType.charAt(0).toUpperCase() + elevationType.slice(1)} Elevation
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowDoorModal(false);
                  setPendingDoorId(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Height (m)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={doorHeightInput}
                    onChange={(e) => setDoorHeightInput(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Width (m)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    value={doorWidthInput}
                    onChange={(e) => setDoorWidthInput(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 flex items-center justify-between">
                <span className="font-medium">Area</span>
                <span className="font-semibold">
                  {(() => {
                    const h = parseFloat(doorHeightInput || '0');
                    const w = parseFloat(doorWidthInput || '0');
                    return h > 0 && w > 0 ? (h * w).toFixed(2) : '0.00';
                  })()}m²
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!pendingDoorId) return;
                  const h = parseFloat(doorHeightInput || '0');
                  const w = parseFloat(doorWidthInput || '0');
                  updateElementDimensions(pendingDoorId, h || 0, w || 0);
                  setShowDoorModal(false);
                  setPendingDoorId(null);
                }}
                className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-semibold"
              >
                Save Door Measurements
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Window Selection Modal */}
      {showWindowSelector && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <RectangleHorizontal className="w-5 h-5 text-blue-600" />
                Configure Window on {elevationType.charAt(0).toUpperCase() + elevationType.slice(1)} Elevation
              </h3>
              <button
                type="button"
                onClick={() => {
                  // If user cancels, remove the pending window
                  if (pendingWindowId) {
                    setPlacedElements(prev => prev.filter(el => el.id !== pendingWindowId));
                    setPendingWindowId(null);
                  }
                  setShowWindowSelector(false);
                  setSelectedFloorPlanWindow(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Status Message */}
              {pendingWindowId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-700">
                    ✨ <strong>Window Placed!</strong> Now configure its dimensions by selecting from your floor plan or entering custom measurements.
                  </p>
                </div>
              )}
              
              {/* Floor Plan Windows Section */}
              {floorPlanWindows.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-600" />
                    Select from Floor Plan Windows
                  </h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-700">
                      🎯 <strong>Quick Selection:</strong> Choose from windows already placed in your floor plan. 
                      Dimensions are automatically populated from the floor plan data.
                    </p>
                  </div>
                  <div className="grid gap-3 max-h-60 overflow-y-auto">
                    {floorPlanWindows.map((window, index) => {
                      const roomName = findRoomNameForWindow(window);
                      
                      return (
                        <div
                          key={window.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedFloorPlanWindow?.id === window.id
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                          onClick={() => setSelectedFloorPlanWindow(window)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Radio button indicator */}
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                selectedFloorPlanWindow?.id === window.id
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-slate-300'
                              }`}>
                                {selectedFloorPlanWindow?.id === window.id && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <RectangleHorizontal className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-medium text-slate-800">
                                  Window {index + 1}
                                  {window.orientation && (
                                    <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                      {window.orientation}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-slate-600">
                                  Floor {window.floor}
                                  {roomName !== 'Unknown Room' && (
                                    <span className="ml-2 text-sm font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                      📍 {roomName}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-slate-800">
                                {(window.width / 1000).toFixed(2)}m × {(window.height / 1000).toFixed(2)}m
                              </div>
                              <div className="text-xs text-slate-500">
                                {((window.width * window.height) / 1000000).toFixed(2)}m²
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {selectedFloorPlanWindow && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => configureFloorPlanWindow(selectedFloorPlanWindow)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Use Selected Window
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Manual Entry Section */}
              <div>
                <h4 className="text-md font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-orange-600" />
                  Or Enter Custom Dimensions
                </h4>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-700">
                    📏 <strong>Manual Entry:</strong> Enter window dimensions manually if not available in floor plan.
                  </p>
                </div>
                <CustomWindowForm onAdd={configureCustomWindow} />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PropertyElevationDiagram; 